import { useState } from 'react';

import { ExternalLinkIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { IntegrationIcon } from '@/components/organisms/Integrations';
import { CommandItem } from '@/components/ui/command';
import { PREVENT_AUTOFILL_PROPS } from '@/components/ui/constants';
import {
  PopoverComboboxContent,
  PopoverComboboxEmpty,
  PopoverComboboxGroup,
  PopoverComboboxInput,
  PopoverComboboxItem,
  PopoverComboboxList,
  PopoverComboboxTrigger,
} from '@/components/ui/custom/PopoverCombobox';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Desk } from '@/shared/types/desk';
import { Integration } from '@/shared/types/integration';

interface IntegrationSelectContentProps {
  integrations: Partial<Integration>[];
  loading: boolean;
  value: string;
  onValueChange: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
}

interface IntegrationGroup {
  deskId: string;
  heading?: string;
  items: Partial<Integration>[];
}

const groupByDesk = (
  integrations: Partial<Integration>[],
  desks: Desk[] | undefined,
  showHeadings: boolean
): IntegrationGroup[] => {
  const byDesk = new Map<string, Partial<Integration>[]>();
  integrations.forEach((integration) => {
    const id = integration.desk_id ?? '';
    const group = byDesk.get(id);
    if (group) {
      group.push(integration);
    } else {
      byDesk.set(id, [integration]);
    }
  });

  const groups: IntegrationGroup[] = (desks ?? [])
    .filter((desk) => byDesk.has(desk.desk_id))
    .map((desk) => ({
      deskId: desk.desk_id,
      heading: showHeadings ? desk.name : undefined,
      items: byDesk.get(desk.desk_id)!,
    }));

  // A sender whose environment is missing from the list still has to be selectable.
  const placed = new Set(groups.map((group) => group.deskId));
  byDesk.forEach((items, id) => {
    if (!placed.has(id)) {
      groups.push({ deskId: id, items });
    }
  });

  return groups;
};

export const IntegrationSelectContent = ({
  integrations,
  loading,
  value,
  onValueChange,
  onOpenChange,
}: IntegrationSelectContentProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const deskId = useDeskId();
  const { desks } = useDesks();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedIntegration = integrations.find((i) => i.integration_id === value);

  const deskNameById = new Map((desks ?? []).map((desk) => [desk.desk_id, desk.name]));

  const searchTerm = search.trim().toLowerCase();
  const filteredIntegrations = searchTerm
    ? integrations.filter(
        (i) =>
          i.name?.toLowerCase().includes(searchTerm) ||
          deskNameById
            .get(i.desk_id ?? '')
            ?.toLowerCase()
            .includes(searchTerm)
      )
    : integrations;

  // Counted before the search, so headings do not come and go as the user types.
  const showHeadings = new Set(integrations.map((i) => i.desk_id)).size > 1;

  const groupedIntegrations = groupByDesk(filteredIntegrations, desks, showHeadings);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearch('');
    }
    onOpenChange?.(isOpen);
  };

  return (
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <PopoverComboboxTrigger open={open} className="h-10">
          {loading ? (
            <Skeleton className="h-4 w-32" />
          ) : selectedIntegration ? (
            <span className="flex items-center gap-2">
              <IntegrationIcon type={selectedIntegration.type} />
              <span className="truncate">{selectedIntegration.name}</span>
            </span>
          ) : (
            <span>{t('common.select_sender')}</span>
          )}
        </PopoverComboboxTrigger>
      </PopoverTrigger>
      <PopoverComboboxContent>
        <PopoverComboboxInput
          {...PREVENT_AUTOFILL_PROPS}
          value={search}
          onValueChange={setSearch}
          placeholder="campaigns.build.search_channels_placeholder"
        />
        <PopoverComboboxList>
          {groupedIntegrations.length > 0
            ? groupedIntegrations.map((group) => (
                <PopoverComboboxGroup key={group.deskId} heading={group.heading}>
                  {group.items.map((integration) => (
                    <PopoverComboboxItem
                      key={integration.integration_id}
                      value={integration.integration_id!}
                      onSelect={() => {
                        onValueChange(integration.integration_id!);
                        setOpen(false);
                      }}
                      selected={value === integration.integration_id}
                    >
                      <div className="flex items-center gap-3">
                        <IntegrationIcon type={integration.type} />
                        <span className="text-foreground">{integration.name}</span>
                      </div>
                    </PopoverComboboxItem>
                  ))}
                </PopoverComboboxGroup>
              ))
            : search && <PopoverComboboxEmpty>{t('common.no_integrations')}</PopoverComboboxEmpty>}
          <PopoverComboboxGroup
            className={groupedIntegrations.length > 0 ? 'border-t pt-1' : ''}
            forceMount
          >
            <CommandItem value="add-channel" className="cursor-pointer" asChild>
              <Link to={`/${slug}/environments/${deskId}/integrations`}>
                <PlusIcon className="text-foreground-muted" />
                <span className="flex-1">{t('campaigns.build.add_new_channel')}</span>
                <ExternalLinkIcon className="h-3.5 w-3.5 text-foreground-muted" />
              </Link>
            </CommandItem>
          </PopoverComboboxGroup>
        </PopoverComboboxList>
      </PopoverComboboxContent>
    </Popover>
  );
};
